import { Injectable } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import {
  atLeastOneValidator,
  getDefaultControlValue,
  ibanValidator,
} from '@gk/gk-modules';
import { ECSControlName } from '../../../../components/electronic-correspondence-statement/models/form/form.model';
import { BaseNewRequestFormService } from '../../../../shared/base-new-request-form/base-new-request-form.service';
import { MCGRWorksEstimatedCostsFormService } from '../../works-estimated-costs/works-estimated-costs-form.service';
import {
  MCGRConservationPermitFormGroup,
  MCGRConstructionPermitFormGroup,
  MCGRLandAndMortgageRegisterNumberAndDocumentsCollectionNumberFormControlName,
  MCGRLandAndMortgageRegisterNumberAndDocumentsCollectionNumberFormGroup,
  MCGRNewRequestControlName,
  MCGRNewRequestFormGroup,
  MCGRPermitFormControlName,
} from './new-request-form.model';

@Injectable()
export class MCGRNewRequestFormService {
  constructor(
    private formBuilder: UntypedFormBuilder,
    private baseNewRequestFormService: BaseNewRequestFormService,
    private mcgrWorksEstimatedCostsFormService: MCGRWorksEstimatedCostsFormService,
  ) {}

  getFormGroup(): FormGroup<MCGRNewRequestFormGroup> {
    return this.formBuilder.group({
      ...this.baseNewRequestFormService.getCommonInvestorFormGroup(
        {
          [ECSControlName.ElectronicCorrespondenceStatement]: false,
        },
        {
          addressFormGroupBasedOnSelectControls: true,
          disableAddressFormInNaturalPersonGroup: true,
        },
      ).controls,
      [MCGRNewRequestControlName.TaskName]: this.formBuilder.control(
        getDefaultControlValue(),
        Validators.required,
      ),
      [MCGRNewRequestControlName.MonumentName]: this.formBuilder.control(
        getDefaultControlValue(),
        Validators.required,
      ),
      [MCGRNewRequestControlName.MonumentRegistryNumber]:
        this.formBuilder.control(getDefaultControlValue(), Validators.required),
      [MCGRNewRequestControlName.MonumentRegistryDate]:
        this.formBuilder.control(getDefaultControlValue()),
      [MCGRNewRequestControlName.DeterminationOfLocation]:
        this.formBuilder.control(getDefaultControlValue()),
      [MCGRNewRequestControlName.JustificationOfImportance]:
        this.formBuilder.control(getDefaultControlValue(), Validators.required),
      [MCGRNewRequestControlName.ContactPerson]: this.formBuilder.control(
        getDefaultControlValue(),
        Validators.required,
      ),
      [MCGRNewRequestControlName.ApplicantAccountNumber]:
        this.formBuilder.control(getDefaultControlValue(), [
          Validators.required,
          ibanValidator(),
        ]),
      [MCGRNewRequestControlName.LegalTitleToMonument]:
        this.formBuilder.control(getDefaultControlValue(), Validators.required),
      [MCGRNewRequestControlName.LandAndMortgageRegisterNumberAndDocumentsCollectionNumber]:
        this.getLandAndMortgageRegisterNumberAndDocumentsCollectionNumberFormGroup(),
      [MCGRNewRequestControlName.ConservationPermit]:
        this.getConservationPermitFormGroup(),
      [MCGRNewRequestControlName.ConstructionPermit]:
        this.getConstructionPermitFormGroup(),
      [MCGRNewRequestControlName.ScopeOfWorks]: this.formBuilder.control(
        getDefaultControlValue(),
        Validators.required,
      ),
      [MCGRNewRequestControlName.JustificationOfWorksPurpose]:
        this.formBuilder.control(getDefaultControlValue(), Validators.required),
      [MCGRNewRequestControlName.WorksDateRange]: this.formBuilder.control(
        getDefaultControlValue(),
      ),
      [MCGRNewRequestControlName.MonumentCare]: this.formBuilder.control(
        getDefaultControlValue(),
      ),
      [MCGRNewRequestControlName.HasBuildingBook]: this.formBuilder.control(
        getDefaultControlValue(),
      ),
      [MCGRNewRequestControlName.HasAnnualTechInspections]:
        this.formBuilder.control(getDefaultControlValue()),
      [MCGRNewRequestControlName.HasFiveYearTechInspections]:
        this.formBuilder.control(getDefaultControlValue()),
      [MCGRNewRequestControlName.FinancialSectorApplication]:
        this.formBuilder.array([]),
      [MCGRNewRequestControlName.OtherActivities]: this.formBuilder.control(
        getDefaultControlValue(),
      ),
      [MCGRNewRequestControlName.WorksEstimatedCosts]:
        this.mcgrWorksEstimatedCostsFormService.getFormGroup(),
      [MCGRNewRequestControlName.OtherConservationActivities]:
        this.formBuilder.array([]),
      [MCGRNewRequestControlName.ExpenditureDeclaration]:
        this.formBuilder.control(getDefaultControlValue(), Validators.required),
      [MCGRNewRequestControlName.AwarenessAndTruthfulnessDeclaration]:
        this.formBuilder.control(getDefaultControlValue(), Validators.required),
    });
  }

  getLandAndMortgageRegisterNumberAndDocumentsCollectionNumberFormGroup(): FormGroup<MCGRLandAndMortgageRegisterNumberAndDocumentsCollectionNumberFormGroup> {
    return this.formBuilder.group(
      {
        [MCGRLandAndMortgageRegisterNumberAndDocumentsCollectionNumberFormControlName.LandAndMortgageRegisterNumber]:
          [getDefaultControlValue()],
        [MCGRLandAndMortgageRegisterNumberAndDocumentsCollectionNumberFormControlName.DocumentsCollectionNumber]:
          [getDefaultControlValue()],
      },
      { validators: atLeastOneValidator() },
    );
  }

  getConservationPermitFormGroup(): FormGroup<MCGRConservationPermitFormGroup> {
    return this.formBuilder.group({
      [MCGRPermitFormControlName.IssuedBy]: this.formBuilder.control(
        getDefaultControlValue(),
        Validators.required,
      ),
      [MCGRPermitFormControlName.Date]: this.formBuilder.control(
        getDefaultControlValue(),
        Validators.required,
      ),
      [MCGRPermitFormControlName.Number]: this.formBuilder.control(
        getDefaultControlValue(),
        Validators.required,
      ),
    });
  }

  getConstructionPermitFormGroup(): FormGroup<MCGRConstructionPermitFormGroup> {
    return this.formBuilder.group({
      [MCGRPermitFormControlName.IssuedBy]: this.formBuilder.control(
        getDefaultControlValue(),
      ),
      [MCGRPermitFormControlName.Date]: this.formBuilder.control(
        getDefaultControlValue(),
      ),
      [MCGRPermitFormControlName.Number]: this.formBuilder.control(
        getDefaultControlValue(),
      ),
      [MCGRPermitFormControlName.Type]: this.formBuilder.control(
        getDefaultControlValue(),
      ),
    });
  }
}
